import { FlexCameraSummary, FlexPreset } from "./types";
import {
  FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION,
  FLEX_IZ_NO_IGNORE_ZONES_OPTION,
} from "./flexIzPresetSelectConstants";

/**
 * ENG-1927: client-side twin of actuate_admin's ENG-1914 guard
 * (`inframap/scheduler/flex_schedule/preset_conflicts.py`).
 *
 * On a fleet site one worker holds one zone config per camera, so a camera
 * cannot run two different presets at the same moment. Admin refuses a
 * `camera-schedule-preset` write that gives a camera a non-null preset on one
 * flex schedule while another enabled schedule of the site gives it a
 * different non-null preset in overlapping hours. camera-ui writes those rows
 * one at a time, so a refusal on row N would leave rows 1..N-1 saved: catch
 * it before the first write.
 *
 * Time semantics copy admin's `schedule_intervals`: every enabled,
 * non-override row becomes week-minute intervals per selected weekday;
 * `end <= start` runs overnight into the next day (Saturday wraps to Sunday);
 * `always_on` is the whole day; a row without a start time is a no-op.
 * "Default" (no preset) never conflicts; the same preset never conflicts.
 * Legacy sites run each schedule in its own deployment and are never checked.
 */

const WEEK_MINUTES = 7 * 1440;

export const FLEET_DEPLOYMENT_TARGET = "watchman_fleet";

export const isFleetSite = (
  site:
    | { deployment_target?: string | null; is_fleet_site?: boolean | null }
    | null
    | undefined
): boolean =>
  site?.is_fleet_site === true ||
  site?.deployment_target === FLEET_DEPLOYMENT_TARGET;

export interface FlexScheduleTimeRow {
  day_of_week?: (string | number)[] | string | null;
  start_time?: string | null;
  end_time?: string | null;
  always_on?: boolean | null;
  enabled?: boolean | null;
  is_override?: boolean | null;
}

export interface FlexScheduleWithTimes {
  id?: number | string;
  display_name?: string | null;
  enabled?: boolean | null;
  schedule?: FlexScheduleTimeRow[] | null;
}

export interface FlexPresetConflict {
  cameraId: number;
  cameraName: string;
  scheduleId: number;
  scheduleName: string;
  presetName: string;
  otherScheduleId: number;
  otherScheduleName: string;
  otherPresetName: string;
  overlapMinutes: number;
}

type Interval = [number, number];

const parseMinutes = (time: string | null | undefined): number | null => {
  if (!time) {
    return null;
  }
  const match = /^\s*(\d{1,2}):(\d{2})/.exec(time);
  if (!match) {
    return null;
  }
  return Number(match[1]) * 60 + Number(match[2]);
};

const weekdays = (value: FlexScheduleTimeRow["day_of_week"]): number[] => {
  if (!value) {
    return [];
  }
  const items = typeof value === "string" ? value.split(",") : value;
  return items
    .map((day) => String(day).trim())
    .filter((day) => day !== "")
    .map(Number)
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
};

/** Week-minute intervals `[a, b)` for one schedule row; `[]` for a no-op row. */
export const scheduleRowIntervals = (row: FlexScheduleTimeRow): Interval[] => {
  const days = weekdays(row.day_of_week);
  if (row.always_on) {
    return days.map((d) => [d * 1440, d * 1440 + 1440] as Interval);
  }
  const start = parseMinutes(row.start_time);
  if (start == null) {
    return [];
  }
  const parsedEnd = parseMinutes(row.end_time);
  let end = parsedEnd == null ? 1440 : parsedEnd;
  if (end <= start) {
    end += 1440; // overnight: spills into the next weekday
  }
  const out: Interval[] = [];
  days.forEach((d) => {
    const a = d * 1440 + start;
    const b = d * 1440 + end;
    if (b <= WEEK_MINUTES) {
      out.push([a, b]);
    } else {
      // Saturday night wraps into Sunday
      out.push([a, WEEK_MINUTES]);
      out.push([0, b - WEEK_MINUTES]);
    }
  });
  return out;
};

/** Intervals of a flex schedule's enabled, non-override rows. */
export const flexScheduleIntervals = (
  schedule: FlexScheduleWithTimes
): Interval[] =>
  (schedule.schedule ?? [])
    .filter((row) => row && row.enabled !== false && row.is_override !== true)
    .flatMap(scheduleRowIntervals);

export const overlapMinutes = (a: Interval[], b: Interval[]): number => {
  let total = 0;
  a.forEach(([a1, b1]) => {
    b.forEach(([a2, b2]) => {
      const lo = Math.max(a1, a2);
      const hi = Math.min(b1, b2);
      if (hi > lo) {
        total += hi - lo;
      }
    });
  });
  return total;
};

const serverPresetId = (localId: string): number | null => {
  const match = /^server-(\d+)$/.exec(localId);
  return match ? Number(match[1]) : null;
};

/**
 * What the selection will be saved as: `null` for Default (a `preset: null`
 * row), otherwise an identity key and a display name.
 *
 * - `"persisted"` (Schedules edit): rows are written with the preset's
 *   `persistedId`, so two local ids of the same saved preset are the same.
 * - `"local"` (onboarding submit): every distinct `(camera, localId)` becomes
 *   its own new preset, so distinct local ids are different presets.
 */
const resolveSelection = (
  cameraId: number,
  localId: string | undefined,
  presetsByCamera: Record<number, FlexPreset[]>,
  presetNamesByCamera: Record<number, Record<number, string>>,
  identity: "persisted" | "local"
): { key: string; name: string } | null => {
  if (!localId || localId === FLEX_IZ_DEFAULT_CAMERA_CONFIG_OPTION) {
    return null;
  }
  const preset = (presetsByCamera[cameraId] || []).find(
    (candidate) => candidate.localId === localId
  );
  const persistedId = preset?.persistedId ?? serverPresetId(localId);
  const name =
    preset?.name?.trim() ||
    (persistedId != null
      ? presetNamesByCamera[cameraId]?.[persistedId]
      : undefined) ||
    (localId === FLEX_IZ_NO_IGNORE_ZONES_OPTION
      ? "No Ignore Zones"
      : persistedId != null
      ? `Preset #${persistedId}`
      : "Unsaved preset");
  const key =
    identity === "persisted" && persistedId != null
      ? `id:${persistedId}`
      : `local:${localId}`;
  return { key, name };
};

/**
 * Every (camera, schedule pair) where the two schedules give the camera
 * different real presets and their windows overlap. Each pair is reported
 * once, in schedule order.
 */
export const findPresetConflicts = ({
  schedules,
  cameras,
  selectedPresetByScheduleCamera,
  presetsByCamera,
  presetNamesByCamera = {},
  identity = "persisted",
}: {
  schedules: FlexScheduleWithTimes[];
  cameras: FlexCameraSummary[];
  selectedPresetByScheduleCamera: Record<string, string>;
  presetsByCamera: Record<number, FlexPreset[]>;
  presetNamesByCamera?: Record<number, Record<number, string>>;
  identity?: "persisted" | "local";
}): FlexPresetConflict[] => {
  const usable = (schedules ?? [])
    .filter((schedule) => schedule && schedule.enabled !== false)
    .map((schedule, index) => ({
      id: Number(schedule.id),
      name: schedule.display_name || `Schedule ${index + 1}`,
      intervals: flexScheduleIntervals(schedule),
    }))
    .filter((schedule) => Number.isFinite(schedule.id));

  const conflicts: FlexPresetConflict[] = [];
  cameras.forEach((camera) => {
    for (let i = 0; i < usable.length; i++) {
      for (let j = i + 1; j < usable.length; j++) {
        const a = usable[i];
        const b = usable[j];
        const presetA = resolveSelection(
          camera.id,
          selectedPresetByScheduleCamera[`${a.id}:${camera.id}`],
          presetsByCamera,
          presetNamesByCamera,
          identity
        );
        const presetB = resolveSelection(
          camera.id,
          selectedPresetByScheduleCamera[`${b.id}:${camera.id}`],
          presetsByCamera,
          presetNamesByCamera,
          identity
        );
        if (!presetA || !presetB || presetA.key === presetB.key) {
          continue;
        }
        const minutes = overlapMinutes(a.intervals, b.intervals);
        if (minutes > 0) {
          conflicts.push({
            cameraId: camera.id,
            cameraName: camera.camera_name || `Camera ${camera.id}`,
            scheduleId: a.id,
            scheduleName: a.name,
            presetName: presetA.name,
            otherScheduleId: b.id,
            otherScheduleName: b.name,
            otherPresetName: presetB.name,
            overlapMinutes: minutes,
          });
        }
      }
    }
  });
  return conflicts;
};

const formatOverlap = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) {
    return `${rest} min`;
  }
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};

/** Same wording as admin's `Conflict.message()`, with the overlap in hours. */
export const presetConflictMessage = (conflict: FlexPresetConflict): string =>
  `Camera "${conflict.cameraName}" would run preset "${conflict.presetName}" on ` +
  `"${conflict.scheduleName}" and preset "${conflict.otherPresetName}" on ` +
  `"${conflict.otherScheduleName}" at the same time ` +
  `(${formatOverlap(
    conflict.overlapMinutes
  )}/week overlap). On a fleet site a ` +
  `camera can only run one preset at a time.`;

export const PRESET_CONFLICT_FIX_HINT =
  "Pick the same preset (or Default) on both schedules for that camera, or change the schedule times so they don't overlap.";

export const presetConflictsAlertText = (
  conflicts: FlexPresetConflict[]
): string =>
  [...conflicts.map(presetConflictMessage), "", PRESET_CONFLICT_FIX_HINT].join(
    "\n"
  );

/**
 * Admin's ENG-1914 refusal is a 400 with `{"cameras": ["Camera \"…\" would
 * run preset …", …]}`. Returns those messages, or null for any other error.
 */
export const presetConflictMessagesFromError = (
  error: unknown
): string[] | null => {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (response?.status !== 400) {
    return null;
  }
  const cameras = (response.data as { cameras?: unknown } | undefined)?.cameras;
  const list = Array.isArray(cameras)
    ? cameras
    : cameras != null
    ? [cameras]
    : [];
  const messages = list
    .map((item) => String(item))
    .filter((message) => message.includes("one preset at a time"));
  return messages.length > 0 ? messages : null;
};
